/*
 * at-search.js — AllTerra Central global search overlay
 * Opens from the header Search trigger; predictive results via the Section Rendering API.
 */

const DEBOUNCE_MS = 200;
const RECENTS_KEY = 'at-search-recents';
const RECENTS_MAX = 6;

class AtSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-at-search-input]');
    this.form = this.querySelector('[data-at-search-form]');
    this.clearBtn = this.querySelector('[data-at-search-clear]');
    this.emptyEl = this.querySelector('[data-at-search-empty]');
    this.resultsEl = this.querySelector('[data-at-search-results]');
    this.recentsEl = this.querySelector('[data-at-search-recents]');
    this.recentsList = this.querySelector('[data-at-search-recent-list]');
    this.sectionId = this.getAttribute('data-predictive-section') || 'at-predictive-search';
    this.debounceTimer = null;
    this.abortCtrl = null;

    this.onKeydown = this.onKeydown.bind(this);
    this.onDocClick = this.onDocClick.bind(this);

    this.bindTriggers();
    this.bindCloseOnOtherNav();
    this.bindInput();
    this.bindSuggestions();

    this.querySelectorAll('[data-at-search-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.form.addEventListener('submit', () => this.commit(this.input.value));
  }

  /* ---- open / close ---- */
  bindTriggers() {
    this.triggers = Array.from(document.querySelectorAll('[data-search-trigger]'));
    this.hoverTimer = null;
    this.triggers.forEach((t) => {
      t.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(t);
      });
      t.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => this.open(t), 80);
      });
      t.addEventListener('pointerleave', () => clearTimeout(this.hoverTimer));
    });
  }

  /* Hovering any other header item (nav item, logo, Speak-to-expert) closes the search. */
  bindCloseOnOtherNav() {
    const header = document.querySelector('at-header');
    if (!header) return;
    const targets = header.querySelectorAll(
      '.at-header__nav-item:not([data-search-trigger]), .at-header__logo, .at-header__expert'
    );
    targets.forEach((el) => {
      el.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        clearTimeout(this.hoverTimer);
        if (!this.hidden) this.close();
      });
    });
  }

  open(trigger) {
    this.openerTrigger = trigger || null;
    this.hidden = false;
    document.documentElement.classList.add('at-search-open');
    const header = document.querySelector('at-header');
    if (header) {
      if (header.close) header.close();
      if (header.drawer && !header.drawer.hidden && header.closeMobile) header.closeMobile();
    }
    this.setHeaderOffset();
    this.triggers.forEach((t) => t.classList.add('is-active'));
    this.renderRecents();
    this.showEmpty();
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerdown', this.onDocClick, { capture: true });
    requestAnimationFrame(() => this.input && this.input.focus());
  }

  close(returnFocus = false) {
    this.hidden = true;
    document.documentElement.classList.remove('at-search-open');
    this.triggers.forEach((t) => t.classList.remove('is-active'));
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerdown', this.onDocClick, { capture: true });
    if (returnFocus && this.openerTrigger && this.openerTrigger.focus) this.openerTrigger.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close(true);
  }

  onDocClick(event) {
    if (!this.hidden && !this.contains(event.target)) this.close();
  }

  setHeaderOffset() {
    const header = document.querySelector('at-header');
    if (header) {
      const h = Math.round(header.getBoundingClientRect().height);
      this.style.setProperty('--at-search-top', `${h}px`);
    }
  }

  /* ---- input ---- */
  bindInput() {
    this.input.addEventListener('input', () => {
      const value = this.input.value.trim();
      this.clearBtn.hidden = value.length === 0;
      clearTimeout(this.debounceTimer);
      if (value.length === 0) {
        this.showEmpty();
        return;
      }
      this.showLoading();
      this.debounceTimer = setTimeout(() => this.fetchResults(value), DEBOUNCE_MS);
    });

    this.clearBtn.addEventListener('click', () => {
      this.input.value = '';
      this.clearBtn.hidden = true;
      this.showEmpty();
      this.input.focus();
    });
  }

  showEmpty() {
    this.emptyEl.hidden = false;
    this.resultsEl.hidden = true;
    this.resultsEl.innerHTML = '';
    this.classList.remove('is-active');
  }

  showResults() {
    this.emptyEl.hidden = true;
    this.resultsEl.hidden = false;
    this.classList.add('is-active');
  }

  showLoading() {
    this.emptyEl.hidden = true;
    this.resultsEl.hidden = false;
    this.classList.add('is-active');
    this.resultsEl.innerHTML =
      '<div class="at-search__loading" role="status" aria-live="polite">' +
      '<span class="at-search__spinner" aria-hidden="true"></span>' +
      '<span>Searching…</span></div>';
  }

  async fetchResults(term) {
    const base = (window.Theme && window.Theme.routes && window.Theme.routes.predictive_search_url) || '/search/suggest';
    const url = new URL(base, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.sectionId);

    if (this.abortCtrl) this.abortCtrl.abort();
    this.abortCtrl = new AbortController();
    try {
      const res = await fetch(url, { signal: this.abortCtrl.signal });
      if (!res.ok) return;
      const html = await res.text();
      this.resultsEl.innerHTML = html;
      this.showResults();
    } catch (err) {
      if (err.name !== 'AbortError') this.showEmpty();
    }
  }

  /* ---- suggestions (curated + predictive) run a search ---- */
  bindSuggestions() {
    this.addEventListener('click', (event) => {
      const el = event.target.closest('[data-at-search-suggestion]');
      if (!el) return;
      const term = el.getAttribute('data-at-search-suggestion');
      if (el.tagName === 'A') {
        this.commit(term);
        return;
      }
      event.preventDefault();
      this.input.value = term;
      this.clearBtn.hidden = false;
      this.commit(term);
      this.fetchResults(term);
    });
  }

  /* ---- recent searches (localStorage) ---- */
  getRecents() {
    try {
      return JSON.parse(localStorage.getItem(RECENTS_KEY)) || [];
    } catch {
      return [];
    }
  }

  saveRecents(list) {
    try {
      localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)));
    } catch {
      /* storage unavailable — ignore */
    }
  }

  commit(term) {
    term = (term || '').trim();
    if (!term) return;
    const list = this.getRecents().filter((t) => t.toLowerCase() !== term.toLowerCase());
    list.unshift(term);
    this.saveRecents(list);
  }

  renderRecents() {
    if (!this.recentsList) return;
    const list = this.getRecents();
    if (list.length === 0) {
      this.recentsEl.hidden = true;
      this.recentsList.innerHTML = '';
      return;
    }
    this.recentsEl.hidden = false;
    this.recentsList.innerHTML = list
      .map(
        (term) => `
      <li class="at-search__recent">
        <button type="button" class="at-search__recent-term" data-at-search-suggestion="${escapeHtml(term)}">
          <span class="at-search__recent-icon">${searchIcon}</span>
          <span>${escapeHtml(term)}</span>
        </button>
        <button type="button" class="at-search__recent-remove" data-at-search-recent-remove="${escapeHtml(term)}" aria-label="Remove">${xIcon}</button>
      </li>`
      )
      .join('');

    this.recentsList.querySelectorAll('[data-at-search-recent-remove]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        const term = btn.getAttribute('data-at-search-recent-remove');
        this.saveRecents(this.getRecents().filter((t) => t !== term));
        this.renderRecents();
      });
    });
  }
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

const searchIcon =
  '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M12.56 11.94L9.82 9.2a5 5 0 10-.62.62l2.74 2.74a.44.44 0 00.62-.62ZM2.19 6.13a3.94 3.94 0 117.87 0 3.94 3.94 0 01-7.87 0Z" fill="currentColor"/></svg>';
const xIcon =
  '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 1.31a5.69 5.69 0 105.69 5.69A5.7 5.7 0 007 1.31Zm2.06 7.13a.44.44 0 01-.62.62L7 7.62 5.56 9.06a.44.44 0 01-.62-.62L6.38 7 4.94 5.56a.44.44 0 01.62-.62L7 6.38l1.44-1.44a.44.44 0 01.62.62L7.62 7Z" fill="currentColor"/></svg>';

if (!customElements.get('at-search')) {
  customElements.define('at-search', AtSearch);
}
